import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HeartPulse, Zap, Timer } from "lucide-react";
import { RingGauge } from "../../components/neu/RingGauge";
import { NeuCard } from "../../components/neu/Neu";
import { AnnualLeaveBar } from "./AnnualLeaveBar";

// كل العدّادات داخل بطاقة موحّدة — 3 حلقات + شريط للسنوية. ألوان متمايزة للنص والحلقة.
const TONES = { sick: "#3b82f6", emergency: "#f59e0b", time_off: "#a855f7" };
const TEXT_CLS = { sick: "text-blue-500", emergency: "text-amber-500", time_off: "text-purple-500" };
const ICONS = { sick: HeartPulse, emergency: Zap, time_off: Timer };

const toneOf = (kind, remaining, allowed) => {
  if (!allowed) return "#9ca3af";
  if (remaining <= 0) return "#ef4444";
  if (remaining / allowed <= 0.25) return "#f97316";
  return TONES[kind];
};

const useSize = () => {
  const [c, setC] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const upd = () => setC(window.innerWidth < 640);
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);
  return c ? 110 : 202;
};

const RingTile = ({ kind, allowed, remaining, sub, labelKey, active, onClick, t, size }) => {
  const tone = toneOf(kind, remaining, allowed);
  const state = active ? "shadow-neu-in" : "hover:-translate-y-0.5";
  return (
    <button type="button" onClick={() => onClick(kind)} data-testid={`leave-tile-${kind}`}
            className={`flex flex-col items-center justify-center gap-2 rounded-2xl bg-background p-3 transition-all sm:gap-3 sm:p-4 ${state}`}>
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <RingGauge testId={`leave-remaining-${kind}`} value={remaining} total={allowed}
                   icon={ICONS[kind]} tone={tone} size={size}
                   label={t("leaves.ofDaysShort", { total: allowed })} />
      </div>
      <p className={`text-xs font-black sm:text-sm ${TEXT_CLS[kind]}`}>{t(labelKey || `leaves.types.${kind}`)}</p>
      {sub ? <span className="text-[10px] font-bold text-muted-foreground">{sub}</span> : null}
    </button>
  );
};

export const LeavesBalanceGrid = ({ balance, timeOff, value, onSelect }) => {
  const { t } = useTranslation();
  const size = useSize();
  const rings = ["sick", "emergency"].map((kind) => {
    const allowed = balance.quotas?.[kind]?.allowed ?? 0;
    const remaining = balance.quotas?.[kind]?.remaining ?? 0;
    return { kind, allowed, remaining, sub: t("leaves.usedDays", { used: Math.max(allowed - remaining, 0) }) };
  });
  return (
    <NeuCard className="grid gap-3 p-4" data-testid="leaves-balance-grid">
      <div className="grid grid-cols-3 items-stretch gap-2 sm:gap-3">
        {rings.map((p) => (
          <RingTile key={p.kind} {...p} active={value === p.kind} onClick={onSelect} t={t} size={size} />
        ))}
        {timeOff ? (
          <RingTile kind="time_off" allowed={timeOff.yearly_limit} remaining={timeOff.remaining_yearly}
                    labelKey="leaves.types.time_off" active={value === "time_off"} onClick={onSelect} t={t} size={size}
                    sub={t("timeOff.availableMonthShort", { avail: timeOff.available_this_month, limit: timeOff.monthly_limit })} />
        ) : null}
      </div>
      <AnnualLeaveBar balance={balance} active={value === "annual"} onClick={() => onSelect("annual")} />
      {timeOff ? (
        <p className="text-center text-[11px] text-muted-foreground" data-testid="fiscal-month">
          {t("timeOff.fiscalPeriod", { start: timeOff.month_start, end: timeOff.month_end })}
        </p>
      ) : null}
    </NeuCard>
  );
};
